import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Loader2, Search, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const GRANTS = [
  { key: 'pro', label: 'Grant Pro-Intelligence', plan: 'pro_intelligence', lifetime: false },
  { key: 'pro_life', label: 'Grant Lifetime Pro', plan: 'pro_intelligence', lifetime: true },
  { key: 'ent', label: 'Grant Enterprise', plan: 'enterprise', lifetime: false },
  { key: 'ent_life', label: 'Grant Lifetime Enterprise', plan: 'enterprise', lifetime: true },
];
const grantBadge = (u) => {
  if (!u.granted_plan_code) return null;
  const name = u.granted_plan_code === 'enterprise' ? 'Enterprise' : 'Pro';
  return u.granted_plan_expires_at ? `${name} · until ${new Date(u.granted_plan_expires_at).toLocaleDateString('en-IN')}` : `Lifetime ${name}`;
};

// Platform-owner only: grant founding accounts a plan (12 months or lifetime).
export const FoundingAccessPanel = ({ open, onOpenChange }) => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/billing/founding-accounts`, auth()).then(r => setUsers(r.data.users || [])).catch(() => setUsers([]));
  }, [open]);

  const shown = useMemo(() => users.filter(u => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())).slice(0, 200), [users, q]);

  const grant = async (u, g) => {
    setBusy(`${u.id}-${g.key}`);
    try {
      const { data } = await axios.post(`${API}/billing/grant-plan`, { user_id: u.id, plan_code: g.plan, lifetime: g.lifetime }, auth());
      setUsers(list => list.map(x => (x.id === u.id ? { ...x, granted_plan_code: data.granted_plan_code, granted_plan_expires_at: data.expires_at } : x)));
      toast.success(`${g.label.replace('Grant ', '')} granted to ${u.name || u.email}`);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Grant failed');
    } finally { setBusy(''); }
  };
  const revoke = async (u) => {
    setBusy(`${u.id}-revoke`);
    try {
      await axios.post(`${API}/billing/grant-plan`, { user_id: u.id, plan_code: 'none' }, auth());
      setUsers(list => list.map(x => (x.id === u.id ? { ...x, granted_plan_code: null, granted_plan_expires_at: null } : x)));
      toast.success('Grant revoked');
    } catch { toast.error('Revoke failed'); } finally { setBusy(''); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto bg-white/80 backdrop-blur-xl" data-testid="founding-access-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Crown size={18} className="text-amber-500" />Founding-account access</SheetTitle>
          <SheetDescription>Grant a plan without billing. Non-lifetime grants last 12 months. Visible only to you.</SheetDescription>
        </SheetHeader>
        <div className="relative mt-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search users…" className="pl-8" data-testid="founding-search" />
        </div>
        <div className="mt-4 space-y-2">
          {shown.map(u => (
            <div key={u.id} className="rounded-2xl border border-white/70 bg-white/60 p-3" data-testid={`founding-row-${u.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-stone-900 truncate">{u.name || u.email}</div>
                  <div className="text-xs text-stone-500 truncate">{u.email} · {u.role}</div>
                </div>
                {grantBadge(u) && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`founding-badge-${u.id}`}>
                    {grantBadge(u)}
                    <button type="button" onClick={() => revoke(u)} className="hover:text-red-600" title="Revoke" data-testid={`founding-revoke-${u.id}`}><X size={11} /></button>
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {GRANTS.map(g => (
                  <button
                    key={g.key}
                    type="button"
                    disabled={!!busy}
                    onClick={() => grant(u, g)}
                    className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-white/70 px-2.5 py-1 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white transition-colors disabled:opacity-50"
                    data-testid={`founding-${g.key}-${u.id}`}
                  >
                    {busy === `${u.id}-${g.key}` && <Loader2 size={11} className="animate-spin" />}{g.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!shown.length && <div className="py-10 text-center text-sm text-stone-500">No users found.</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
};
